import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import LargeLoading from '@/components/common/LargeLoading';
import WeeklyPsychologistCalendar from '../components/psychologist/WeeklyPsychologistCalendar';
import { CalendarConfigDropdown } from '../components/psychologist/calendar/CalendarConfigDropdown';
import { CalendarSchedulingBanner } from '../components/psychologist/calendar/CalendarSchedulingBanner';
import { HolidayAgendaNotice } from '../components/psychologist/calendar/HolidayAgendaNotice';
import { CalendarUnsavedModal } from '../components/psychologist/calendar/CalendarUnsavedModal';
import { useCalendarEditMode } from '../components/psychologist/calendar/hooks/useCalendarEditMode';
import { useCalendarSession } from '../components/psychologist/calendar/hooks/useCalendarSession';
import { ViewSelector, type CalendarView } from '../pages/psychologist/tabs/calendar/components/ViewSelector';
import { DayView } from '../pages/psychologist/tabs/calendar/views/DayView';
import { MonthView } from '../pages/psychologist/tabs/calendar/views/MonthView';
import SessionDetailsModal from '../components/psychologist/calendar/SessionDetailsModal';
import { isoToZoneParts } from '../components/psychologist/calendar/timeZoneHelpers';
import TimezoneAgendaUpdatedModal from '../components/psychologist/calendar/TimezoneAgendaUpdatedModal';
import { dismissTimezoneAgendaBanner, getPendingTimezoneAgendaChange } from '../utils/timezoneAgendaNotice';
import { PatientDetailModal } from '../pages/psychologist/tabs/patients/components/PatientDetailModal';
import type { Patient } from '../pages/psychologist/tabs/patients/components/PatientTable';
import { useAppShell } from '../contexts/AppShellContext';
import { usePsychologistData } from '../hooks/usePsychologistData';
import { BA_TIMEZONE } from '../utils/timezone';
import type { SessionLite } from '../types/session-lite';
import type { SessionWithPsychologist, SessionStatus } from '../components/psychologist/calendar/data';

const VIEWS: CalendarView[] = ['month', 'week', 'day'];

/** Deep links que la Agenda de la web entiende y la app también: abrir una sesión, o agendar con un paciente. */
interface DeepLinks {
  /** `?process=<id>&book=1`: abre el agendado con ese paciente. */
  preloadProcessId?: string;
  preloadForceSelect: boolean;
  /** `?session=<id>&date=YYYY-MM-DD`: abre el detalle de esa sesión. */
  sessionId?: string;
  /** `?openForPatient=<mail>&date=YYYY-MM-DD`: abre la sesión de ese paciente en esa fecha (el "ir al calendario" de Pacientes). */
  patientEmail?: string;
  sessionDate?: string;
}

function readDeepLinks(params: URLSearchParams): DeepLinks {
  return {
    preloadProcessId: params.get('process') || undefined,
    preloadForceSelect: params.get('book') === '1',
    sessionId: params.get('session') || undefined,
    patientEmail: params.get('openForPatient') || undefined,
    sessionDate: params.get('date') || undefined,
  };
}

/**
 * La vista inicial sale de `?view=` (igual que la Agenda de la web); sin valor válido, la diaria. Un link que abre una
 * sesión o agenda con un paciente (`?session=` / `?openForPatient=` / `?process=`) siempre arranca en la semanal: es la que los resuelve.
 */
function initialView(params: URLSearchParams, links: DeepLinks): CalendarView {
  if (links.sessionId || links.patientEmail || links.preloadProcessId) return 'week';
  const value = params.get('view');
  return VIEWS.includes(value as CalendarView) ? (value as CalendarView) : 'day';
}

const noop = () => undefined;

function monthLabel(date: Date): string {
  const name = date.toLocaleDateString('es-ES', { month: 'long' });
  return `${name.charAt(0).toUpperCase()}${name.slice(1)} ${date.getFullYear()}`;
}

const arrowClasses =
  'grid h-11 w-11 place-items-center rounded-full border-0 bg-transparent text-3xl leading-none text-[#6D57BC] hover:bg-violet-50';

/**
 * Agenda de la app mobile: vista diaria, semanal y mensual — las mismas de la Agenda de la web a ancho de celular, con el
 * mismo selector de vista — y desde ella se puede AGENDAR, REPROGRAMAR y CANCELAR sesiones, reutilizando los modales de la
 * web (`SelectPatientAndTypeModal`, `BookSessionModal`, reprogramación y cancelación vía `SessionDetailsModal`).
 * También se CONFIGURAN los horarios (recurrentes y de un día específico) y se BLOQUEAN espacios, con el mismo menú "Horarios" y los
 * mismos paneles de la web: viven en la semanal, así que desde la diaria/mensual se pasa a ella. Quedan afuera los tours de
 * onboarding y todo lo que sea el Meet (solo desktop).
 * Tocar una sesión abre su información; de ahí se cancela, se reprograma o se va a "Ver proceso".
 */
export function PwaAgenda() {
  const [searchParams, setSearchParams] = useSearchParams();
  // Los deep links se leen UNA vez al montar: la semanal borra el query string al montarse (`useCalendarNavigation`).
  const [deepLinks, setDeepLinks] = useState<DeepLinks>(() => readDeepLinks(searchParams));
  // La vista vive en ESTADO y se refleja en `?view=`: la semanal borra el query string, así que la URL no sirve como única
  // fuente de verdad.
  const [view, setView] = useState<CalendarView>(() => initialView(searchParams, readDeepLinks(searchParams)));
  const psychologist = usePsychologistData();
  const therapistTz = psychologist?.timezone || BA_TIMEZONE;
  const session = useCalendarSession();
  // Configurar horarios / bloquear: mismo estado que en la web. Los cambios sin guardar se avisan al marco (`PwaShell`),
  // que pide confirmar antes de cambiar de sección.
  const { setCalendarDirty } = useAppShell();
  const editMode = useCalendarEditMode({ setCalendarDirty });
  const [configDropdownOpen, setConfigDropdownOpen] = useState(false);
  // Tras cambiar el huso horario en Perfil, la Agenda avisa que las sesiones se muestran en el huso nuevo (igual que la web).
  const [timezoneChange, setTimezoneChange] = useState(() => getPendingTimezoneAgendaChange());
  const closeTimezoneModal = useCallback(() => {
    dismissTimezoneAgendaBanner();
    setTimezoneChange(null);
  }, []);

  const [detailPatient, setDetailPatient] = useState<Patient | null>(null);
  const [monthDate, setMonthDate] = useState<Date>(() => new Date());
  const dayTodayRef = useRef<(() => void) | null>(null);
  const weekTodayRef = useRef<(() => void) | null>(null);

  // Modo agendar / reprogramar: lo informa la semanal; el banner y el botón de cancelar salen de acá.
  const [openScheduleModalTick, setOpenScheduleModalTick] = useState(0);
  const [cancelSchedulingTick, setCancelSchedulingTick] = useState(0);
  const [isSchedulingMode, setIsSchedulingMode] = useState(false);
  const [schedulingPatientName, setSchedulingPatientName] = useState<string | undefined>(undefined);
  const [isRescheduleMode, setIsRescheduleMode] = useState(false);
  const [reschedulingSessionInfo, setReschedulingSessionInfo] = useState<{ patientName: string; date: string } | null>(null);
  // Sesión que la semanal tiene que abrir sola (desde la diaria/mensual: "Reprogramar" lleva a la semanal).
  const [autoOpen, setAutoOpen] = useState<{ id: string; date: string; view: 'details' | 'reschedule' } | null>(null);

  const { handleCloseSessionModal } = session;

  const changeView = useCallback(
    (next: CalendarView) => {
      setView(next);
      handleCloseSessionModal();
      // Un pedido de "Agendar sesión" ya atendido no puede quedar guardado: la semanal se vuelve a montar al volver a ella y
      // lo tomaría como uno nuevo (abriría el selector de paciente sola).
      setOpenScheduleModalTick(0);
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);
          params.set('view', next);
          // La vista diaria ubica sola su fecha; en mes y semana un `date` viejo no significa nada.
          if (next !== 'day') params.delete('date');
          return params;
        },
        { replace: true },
      );
    },
    [setSearchParams, handleCloseSessionModal],
  );

  const openPatient = useCallback(
    (patientId: string, processId?: string, patientName?: string, therapyType?: string) =>
      setDetailPatient({
        id: patientId,
        processId: processId || patientId,
        name: patientName || '',
        service: therapyType,
        totalSessions: 0,
      }),
    [],
  );

  // La diaria y la mensual entregan la sesión "liviana"; el modal de detalle trabaja con la completa
  // (misma conversión que hace la Agenda de la web).
  const openSessionDetails = useCallback(
    (lite: SessionLite) => {
      const rawDate = lite.date ?? '';
      const parts = isoToZoneParts(rawDate, therapistTz);
      session.setSelectedSession({
        id: lite.id,
        psychologistId: psychologist?.id ?? '',
        date: parts.date,
        time: parts.time,
        modality: '',
        status: lite.status as SessionStatus,
        patient: { name: lite.patientName, email: '', timezone: lite.patientTimezone },
        specialty: lite.therapyType,
        process_id: lite.process,
        duration: 60,
        origISO: rawDate,
      });
      session.setIsSessionModalOpen(true);
    },
    // `session` cambia en cada render (es un objeto nuevo): solo importan sus setters, que son estables.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [psychologist?.id, therapistTz, session.setSelectedSession, session.setIsSessionModalOpen],
  );

  // "Ver proceso" desde el detalle de la sesión: cierra el modal y abre el detalle del paciente.
  const viewProcessOf = useCallback(
    (s: SessionWithPsychologist) => {
      handleCloseSessionModal();
      openPatient(s.process_id || '', s.process_id || '', s.patient?.name || s.patient?.email, s.specialty);
    },
    [handleCloseSessionModal, openPatient],
  );

  // "Agendar sesión": el agendado vive en la semanal. Desde otra vista se pasa a la semanal Y se abre el selector de paciente
  // en el mismo toque (la semanal espera a terminar de cargar: `deferScheduleTickWhileBusy`).
  const scheduleSession = useCallback(() => {
    if (view !== 'week') changeView('week');
    setOpenScheduleModalTick(Date.now());
  }, [view, changeView]);

  // "Reprogramar" desde la diaria/mensual: se pasa a la semanal con esa sesión abierta directo en la reprogramación.
  const rescheduleInWeek = useCallback(
    (s: SessionWithPsychologist) => {
      handleCloseSessionModal();
      setAutoOpen({ id: s.id, date: s.date, view: 'reschedule' });
      changeView('week');
    },
    [handleCloseSessionModal, changeView],
  );

  const handleRescheduleModeChange = useCallback(
    (isReschedule: boolean, s: { patient: { name?: string; email: string }; date: string } | null) => {
      setIsRescheduleMode(isReschedule);
      setReschedulingSessionInfo(
        isReschedule && s ? { patientName: s.patient.name || s.patient.email, date: s.date.split('-').reverse().join('/') } : null,
      );
    },
    [],
  );

  const handleSchedulingModeChange = useCallback((isScheduling: boolean, patientName?: string) => {
    setIsSchedulingMode(isScheduling);
    setSchedulingPatientName(isScheduling ? patientName : undefined);
  }, []);

  // Entrar a editar horarios: mismo retraso que en la web (la pantalla "Cargando configuración" evita un salto brusco).
  const startEditing = useCallback(() => {
    editMode.setIsTogglingEdit(true);
    window.setTimeout(() => {
      editMode.setCalendarEditMode(true);
      editMode.setIsTogglingEdit(false);
    }, 450);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editMode.setIsTogglingEdit, editMode.setCalendarEditMode]);

  const handleStartRecurrent = () => {
    if (editMode.isTogglingEdit) return;
    startEditing();
  };

  const handleStartSpecificDay = (date = '') => {
    editMode.setSpecificDayDate(date);
    if (!editMode.calendarEditMode) startEditing();
  };

  const handleStartBlock = () => {
    editMode.setSpecificDayDate(null);
    editMode.setOpenBlockModalTick(true);
    window.setTimeout(() => {
      if (!editMode.calendarEditMode) editMode.setCalendarEditMode(true);
    }, 450);
  };

  // Los paneles de configuración viven en la semanal: desde la diaria/mensual se pasa a ella primero (igual que la web).
  const switchToWeekThen = (action: () => void) => {
    if (view !== 'week') changeView('week');
    window.setTimeout(action, 200);
  };

  // "Salir sin guardar" del aviso de cambios sin guardar.
  const handleDiscard = () => {
    editMode.setShowUnsavedModal(false);
    editMode.setScheduleHasChanges(false);
    editMode.setSpecificDayHasChanges(false);
    editMode.setBlockHasChanges(false);
    if (editMode.pendingCloseCallback) {
      editMode.pendingCloseCallback();
      editMode.setPendingCloseCallback(null);
    } else {
      editMode.setCalendarEditMode(false);
      editMode.setOpenBlockModalTick(false);
      editMode.setSpecificDayDate(null);
    }
  };

  // "Cancelar" / X del panel de Horario fijo. En la web lo atiende el hook de tours (`useCalendarTour`), que la app mobile no
  // monta; Día específico y Bloquear sí lo resuelven en `useCalendarEditMode`. Sin esto el panel no se podía cerrar.
  const { scheduleHasChanges, setShowUnsavedModal, setIsExitingEdit, setCalendarEditMode, setOpenBlockModalTick, setSpecificDayDate } =
    editMode;
  useEffect(() => {
    const handler = (e: Event) => {
      const wasSaved = (e as CustomEvent<{ wasSaved?: boolean }>).detail?.wasSaved;
      if (scheduleHasChanges && !wasSaved) {
        setShowUnsavedModal(true);
        return;
      }
      setIsExitingEdit(true);
      window.setTimeout(() => {
        setIsExitingEdit(false);
        setCalendarEditMode(false);
        setOpenBlockModalTick(false);
        setSpecificDayDate(null);
      }, 1000);
    };
    window.addEventListener('psi-work-schedule-cancel', handler);
    return () => window.removeEventListener('psi-work-schedule-cancel', handler);
  }, [scheduleHasChanges, setShowUnsavedModal, setIsExitingEdit, setCalendarEditMode, setOpenBlockModalTick, setSpecificDayDate]);

  const goToToday = () => {
    if (view === 'month') setMonthDate(new Date());
    else if (view === 'week') weekTodayRef.current?.();
    else dayTodayRef.current?.();
  };

  const calendarPsychologist = useMemo(
    () => ({
      id: psychologist?.id ?? '',
      specialties: psychologist?.specialties?.length ? psychologist.specialties : ['anxiety', 'stress'],
      statusId: psychologist?.status?.id,
    }),
    [psychologist?.id, psychologist?.specialties, psychologist?.status?.id],
  );

  const sessionModalOpen = session.isSessionModalOpen;

  return (
    <section className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <ViewSelector currentView={view} onViewChange={changeView} />

        <div className="inline-flex items-center gap-1">
          {view === 'month' && (
            <>
              <button
                type="button"
                aria-label="Mes anterior"
                className={arrowClasses}
                onClick={() => setMonthDate((d) => new Date(d.getFullYear(), d.getMonth() - 1, 1))}
              >
                ‹
              </button>
              <span className="whitespace-nowrap text-[0.9375rem] font-bold text-[#6D57BC]">{monthLabel(monthDate)}</span>
              <button
                type="button"
                aria-label="Mes siguiente"
                className={arrowClasses}
                onClick={() => setMonthDate((d) => new Date(d.getFullYear(), d.getMonth() + 1, 1))}
              >
                ›
              </button>
            </>
          )}
          <Button variant="outline" size="sm" onClick={goToToday}>
            Hoy
          </Button>
        </div>
      </div>

      {/* Mismo menú que la web: "Agendar" + "Horarios" (recurrente / día específico / bloquear). Se oculta solo al editar o agendar. */}
      <CalendarConfigDropdown
        calendarEditMode={editMode.calendarEditMode}
        isSchedulingMode={isSchedulingMode}
        isRescheduleMode={isRescheduleMode}
        isTogglingEdit={editMode.isTogglingEdit}
        configDropdownOpen={configDropdownOpen}
        setConfigDropdownOpen={setConfigDropdownOpen}
        configDropdownRef={editMode.configDropdownRef}
        runTour={false}
        onReplayTour={noop}
        onScheduleSession={scheduleSession}
        onStartRecurrent={() => switchToWeekThen(handleStartRecurrent)}
        onStartSpecificDay={() => switchToWeekThen(() => handleStartSpecificDay())}
        onStartBlock={() => switchToWeekThen(handleStartBlock)}
        hideHelp
      />

      {/* Aviso de feriados próximos: el mismo de la Agenda web. */}
      <HolidayAgendaNotice className="mb-3" />

      <CalendarSchedulingBanner
        isSchedulingMode={isSchedulingMode}
        isRescheduleMode={isRescheduleMode}
        reschedulingSessionInfo={reschedulingSessionInfo}
        schedulingPatientName={schedulingPatientName}
        onCancel={() => setCancelSchedulingTick(Date.now())}
      />

      {view === 'month' && (
        <MonthView
          key={session.refreshKey}
          currentMonthDate={monthDate}
          onNavigateToDay={(iso) => {
            setView('day');
            setSearchParams(
              (prev) => {
                const params = new URLSearchParams(prev);
                params.set('view', 'day');
                params.set('date', iso);
                return params;
              },
              { replace: true },
            );
          }}
          onSessionClick={openSessionDetails}
          isMobile
          onSwipeNext={() => setMonthDate((d) => new Date(d.getFullYear(), d.getMonth() + 1, 1))}
          onSwipePrev={() => setMonthDate((d) => new Date(d.getFullYear(), d.getMonth() - 1, 1))}
          therapistTimezone={therapistTz}
        />
      )}

      {view === 'week' && (
        <WeeklyPsychologistCalendar
          key={session.refreshKey}
          psychologist={calendarPsychologist}
          editMode={editMode.calendarEditMode}
          onEditModeToggle={() => editMode.setCalendarEditMode(!editMode.calendarEditMode)}
          // Igual que la Agenda de la web: datos frescos (sin esto reusaría una copia guardada de la semana), salvo mientras se
          // edita: ahí un refresco pisaría lo que se está cargando.
          forceRefresh={!editMode.calendarEditMode}
          openScheduleModalTick={openScheduleModalTick}
          deferScheduleTickWhileBusy
          isCancelling={session.isCancelling}
          isSessionModalOpen={session.isSessionModalOpen}
          setIsSessionModalOpen={session.setIsSessionModalOpen}
          handleCancelSession={session.handleCancelSession}
          handleCloseSessionModal={session.handleCloseSessionModal}
          selectedSession={session.selectedSession}
          setSelectedSession={session.setSelectedSession}
          openBlockModalTick={editMode.openBlockModalTick}
          onCloseBlockModal={editMode.handleCloseBlockModal}
          openCreateModalForDate={editMode.specificDayDate}
          onCloseCreateModal={() => {
            editMode.setSpecificDayDate(null);
            editMode.setCalendarEditMode(false);
          }}
          onScheduleHasChangesChange={editMode.handleScheduleHasChangesChange}
          isExitingEdit={editMode.isExitingEdit}
          onSpecificDayHasChangesChange={editMode.setSpecificDayHasChanges}
          onBlockHasChangesChange={editMode.setBlockHasChanges}
          todayFnRef={weekTodayRef}
          onSessionViewProcess={viewProcessOf}
          onSchedulingModeChange={handleSchedulingModeChange}
          onRescheduleModeChange={handleRescheduleModeChange}
          cancelSchedulingTick={cancelSchedulingTick}
          preloadProcessId={deepLinks.preloadProcessId}
          preloadForceSelect={deepLinks.preloadForceSelect}
          onPreloadConsumed={() => setDeepLinks((d) => ({ ...d, preloadProcessId: undefined, preloadForceSelect: false }))}
          autoOpenSessionId={autoOpen?.id ?? deepLinks.sessionId}
          autoOpenDate={autoOpen?.date ?? deepLinks.sessionDate}
          autoOpenSessionView={autoOpen?.view}
          autoOpenPatientEmail={deepLinks.patientEmail}
          onAutoOpenConsumed={() => {
            setDeepLinks((d) => ({ ...d, patientEmail: undefined }));
            // El mail del paciente es un dato personal: no queda en la barra de direcciones ni en el historial.
            setSearchParams(
              (prev) => {
                const params = new URLSearchParams(prev);
                params.delete('openForPatient');
                return params;
              },
              { replace: true },
            );
          }}
          onAutoOpenSessionConsumed={() => {
            setAutoOpen(null);
            setDeepLinks((d) => ({ ...d, sessionId: undefined, sessionDate: undefined }));
          }}
        />
      )}

      {view === 'day' && (
        <DayView
          key={session.refreshKey}
          psychologist={psychologist}
          onPatientClick={openPatient}
          onSchedule={scheduleSession}
          onOpenSessionModal={openSessionDetails}
          goToTodayRef={dayTodayRef}
        />
      )}

      {/* En la semanal el detalle de la sesión lo monta la propia semanal. */}
      {view !== 'week' && sessionModalOpen && session.selectedSession && (
        <SessionDetailsModal
          key={session.selectedSession.id}
          open={sessionModalOpen}
          onClose={session.handleCloseSessionModal}
          session={session.selectedSession}
          onCancel={session.handleCancelSession}
          psychologistId={psychologist?.id ?? undefined}
          therapistTimezone={therapistTz}
          onViewProcess={() => viewProcessOf(session.selectedSession as SessionWithPsychologist)}
          onStartReschedule={rescheduleInWeek}
        />
      )}

      <TimezoneAgendaUpdatedModal
        open={timezoneChange !== null}
        prevTimezone={timezoneChange?.prevTz ?? null}
        newTimezone={timezoneChange?.newTz ?? null}
        onClose={closeTimezoneModal}
      />

      <LargeLoading open={view !== 'week' && session.isCancelling} message="Cancelando sesión" />
      <LargeLoading open={editMode.isTogglingEdit} message="Cargando configuración" />

      <CalendarUnsavedModal
        open={editMode.showUnsavedModal}
        onKeepEditing={() => {
          editMode.setShowUnsavedModal(false);
          editMode.setPendingCloseCallback(null);
        }}
        onDiscard={handleDiscard}
      />

      {detailPatient && <PatientDetailModal patient={detailPatient} onClose={() => setDetailPatient(null)} />}
    </section>
  );
}
